class ProductRecommendations extends HTMLElement {
  observer = undefined;
  _fetchStarted = false;

  constructor() {
    super();
  }

  connectedCallback() {
    const productId = this.getProductId();
    this.initializeRecommendations(productId);
  }

  getProductId() {
    return (
      this.getAttribute("data-product-id") ||
      this.dataset.productId ||
      document
        .querySelector('[id^="MainProduct-"][data-product-id]')
        ?.getAttribute("data-product-id") ||
      document.body?.dataset?.productId ||
      ""
    );
  }

  getSectionId() {
    const raw = (
      this.getAttribute("data-section-id") ||
      this.dataset.sectionId ||
      ""
    ).trim();
    return raw || "recommendations";
  }

  resolveFetchUrl(baseUrl) {
    if (!baseUrl) return "";
    if (/^https?:\/\//i.test(baseUrl)) return baseUrl;
    const root =
      typeof window !== "undefined" && window.Shopify?.routes?.root != null
        ? window.Shopify.routes.root
        : "/";
    if (baseUrl.startsWith("/")) {
      const prefix = root.endsWith("/") ? root.slice(0, -1) : root;
      return `${prefix}${baseUrl}`;
    }
    return `${root}${baseUrl}`;
  }

  initializeRecommendations(productId) {
    this.observer?.disconnect();

    const run = () => {
      const resolvedProductId = productId || this.getProductId();
      if (!resolvedProductId || this._fetchStarted) return;
      this.loadRecommendations(resolvedProductId);
    };

    requestAnimationFrame(() => {
      const rect = this.getBoundingClientRect();
      const vh = window.innerHeight || 0;
      const near =
        rect.top < vh + 500 &&
        rect.bottom > -500 &&
        (rect.width > 0 || rect.height > 0);

      if (near) {
        run();
        return;
      }

      this.observer = new IntersectionObserver(
        (entries, observer) => {
          if (!entries[0]?.isIntersecting) return;
          observer.disconnect();
          run();
        },
        { rootMargin: "0px 0px 400px 0px", threshold: 0 }
      );
      this.observer.observe(this);
    });
  }

  parseRecommendationsMarkup(text) {
    const shell = document.createElement("div");
    shell.innerHTML = text;

    const nested =
      shell.querySelector(".shopify-section product-recommendations") ||
      shell.querySelector("product-recommendations");
    if (nested?.innerHTML?.trim()) {
      return nested.innerHTML;
    }

    const recSection = shell.querySelector(".recommendations-section");
    if (recSection?.innerHTML?.trim()) {
      return recSection.outerHTML;
    }

    const recBlock =
      shell.querySelector(".recommendation-section") ||
      shell.querySelector(".recommendations-empty");
    if (recBlock?.outerHTML?.trim()) {
      return recBlock.outerHTML;
    }

    const grid = shell.querySelector(".product-recommendations");
    if (grid?.innerHTML?.trim()) {
      return grid.innerHTML;
    }

    const sectionRoot = shell.querySelector(".shopify-section");
    if (sectionRoot?.innerHTML?.trim()) {
      return sectionRoot.innerHTML;
    }

    return "";
  }

  loadRecommendations(productId) {
    if (this._fetchStarted) return;
    const baseUrl = this.getAttribute("data-url") || this.dataset.url || "";
    const sectionId = this.getSectionId();
    const resolvedProductId = productId || this.getProductId();

    if (!resolvedProductId) {
      return;
    }

    this._fetchStarted = true;
    this.observer?.disconnect();

    const sep = baseUrl.includes("?") ? "&" : "?";
    const url = `${this.resolveFetchUrl(
      baseUrl
    )}${sep}product_id=${encodeURIComponent(
      resolvedProductId
    )}&section_id=${encodeURIComponent(sectionId)}`;

    fetch(url)
      .then((response) => {
        if (!response.ok) {
          throw new Error(
            `Recommendations HTTP ${response.status}: ${response.statusText}`
          );
        }
        return response.text();
      })
      .then((text) => {
        const markup = this.parseRecommendationsMarkup(text);

        if (markup.trim().length) {
          this.innerHTML = markup;
        }

        const hasCards =
          this.querySelector(".grid__item") ||
          this.querySelector(".recommendation-item") ||
          this.querySelector(".card-product");

        const hasEmptyState = this.querySelector(
          "[data-recommendations-empty]"
        );
        const hasLoading = this.querySelector("[data-recommendations-loading]");

        if (hasCards || hasEmptyState) {
          this.classList.add("product-recommendations--loaded");
        } else if (hasLoading) {
          /* SSR skeleton — keep it; do not strip (was causing blank hosts). */
        } else if (!markup.trim().length) {
          /* Unparseable or empty body — leave existing DOM if any */
        }

        if (hasCards && typeof window.initProductCards === "function") {
          window.initProductCards(this);
        }

        if (
          !this.querySelector("slideshow-component") &&
          this.classList.contains("complementary-products")
        ) {
          this.remove();
        }
      })
      .catch((e) => {
        console.error(e);
        this._fetchStarted = false;
      })
      .finally(() => {
        try {
          document
            .querySelectorAll("[data-wishlist-toggle]")
            .forEach((button) => {
              const wishlist =
                JSON.parse(localStorage.getItem("wishlist") || "[]") || [];
              const productHandle = button.dataset.productHandle;
              if (wishlist.includes(productHandle)) {
                button.classList.add("in-wishlist", "remove");
                button.setAttribute("aria-label", "remove to wishlist");
              }
            });
        } catch (_) {
          /* ignore bad wishlist JSON */
        }

        const allCards = document.querySelectorAll(".card-product");

        allCards.forEach((product) => {
          product.addEventListener("click", (e) => {
            const colorOption = e.target.closest(
              ".card-product__color-option:not(.disabled)"
            );
            if (colorOption && typeof handleColorSwatch === "function") {
              e.preventDefault();
              handleColorSwatch(colorOption, product);
            }
          });
        });
      });
  }
}

customElements.define("product-recommendations", ProductRecommendations);
